/**
 * Главный экран: одна сводка по аккаунту.
 *
 * Подписок может быть несколько — каждая даёт свои устройства. Здесь только
 * общее; сами подписки, их подключение и покупка новых — на экране
 * «Мои подписки»: дублировать их на главной значит показывать одно
 * и то же в двух местах.
 */
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router';

import { useSubscription } from '@/entities/subscription/queries';
import { describeState, toneColor } from '@/entities/subscription/state';
import type { AccountState, Subscription } from '@/shared/api/schemas';
import { formatTraffic } from '@/shared/lib/format';
import { config } from '@/shared/config/env';
import { isGuest } from '@/shared/lib/guest';
import { openExternal } from '@/shared/lib/links';
import { Screen } from '@/shared/ui/Screen';
import { ActionGrid, ActionTile } from '@/shared/ui/kit/ActionTile';
import { Button } from '@/shared/ui/kit/Button';
import { Icon } from '@/shared/ui/kit/Icon';
import { LegalLinks } from '@/shared/ui/kit/LegalLinks';
import { Servers } from '@/shared/ui/kit/Servers';
import { Stat, StatGrid } from '@/shared/ui/kit/Stat';

import styles from './HomePage.module.css';

export function HomePage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useSubscription();

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      {data ? (
        <HomeContent
          data={data}
          onOpenPlans={() => void navigate('/plans')}
          onOpenSubscriptions={() => void navigate('/subscriptions')}
          onOpenReferral={() => void navigate('/referral')}
        />
      ) : null}
    </Screen>
  );
}

/**
 * Заголовок коробки — что происходит, одним выражением.
 *
 * Дату показывает листок календаря, поэтому в тексте нет ни даты, ни числа
 * дней: рядом с листком они повторяли бы его же.
 */
const TITLES: Record<AccountState, string> = {
  no_subscription: 'Нет подписки',
  trial: 'Пробный период',
  active: 'Подписка активна',
  expiring_soon: 'Скоро закончится',
  grace: 'Последний день',
  expired: 'Подписка закончилась',
  traffic_exhausted: 'Трафик закончился',
  banned: 'Доступ ограничен',
};

/** Дата на листке зачёркнута: срок уже прошёл. */
const PAST: ReadonlySet<AccountState> = new Set(['grace', 'expired']);

function monthShort(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', { month: 'short' })
    .format(new Date(iso))
    .replace('.', '')
    .toUpperCase();
}

function dayOfMonth(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit' }).format(new Date(iso));
}

/** Листок календаря. Без даты — пустой листок, у бессрочной — знак бесконечности. */
function Sheet({
  iso,
  tone,
  past,
  lifetime,
  gift = false,
}: {
  iso: string | null;
  tone: string;
  past: boolean;
  lifetime: boolean;
  /** Режим просмотра: вместо даты — подарок, пробный период ждёт в боте. */
  gift?: boolean;
}) {
  if (gift) {
    return (
      <div className={styles.sheet} style={{ '--sheet-tone': tone } as CSSProperties} aria-hidden>
        <div className={styles.month}>3 ДНЯ</div>
        <div className={styles.day}>
          <Icon name="gift" size={30} />
        </div>
      </div>
    );
  }
  return (
    <div className={styles.sheet} style={{ '--sheet-tone': tone } as CSSProperties} aria-hidden>
      <div className={styles.month}>{iso ? monthShort(iso) : '—'}</div>
      <div className={past ? `${styles.day} ${styles.past}` : iso || lifetime ? styles.day : `${styles.day} ${styles.empty}`}>
        {lifetime ? '∞' : iso ? dayOfMonth(iso) : '—'}
      </div>
    </div>
  );
}

function HomeContent({
  data,
  onOpenPlans,
  onOpenSubscriptions,
  onOpenReferral,
}: {
  data: Subscription;
  onOpenPlans: () => void;
  onOpenSubscriptions: () => void;
  onOpenReferral: () => void;
}) {
  const view = describeState(data.state);
  const accessible = data.items.filter((item) => item.has_access);
  // Подписки приходят ближайшей к окончанию первой: при открытом доступе
  // на листке её дата, при закрытом — дата последней закончившейся.
  const shown = accessible[0] ?? data.items[0];
  const lifetime = shown?.is_lifetime ?? false;
  const hasAny = data.items.length > 0;
  const urgent = data.state === 'expiring_soon' || data.state === 'grace';

  const subsLabel = data.items.length > 1 ? `Мои подписки · ${data.items.length}` : 'Моя подписка';
  const devices = data.device_limit_total === null ? '∞' : `0 / ${data.device_limit_total}`;

  return (
    <>
      <section className={styles.hero}>
        <div className={styles.row}>
          <Sheet
            iso={shown?.expires_at ?? null}
            tone={hasAny || isGuest() ? toneColor(isGuest() ? 'positive' : view.tone) : 'var(--surface-sunken)'}
            gift={isGuest()}
            past={PAST.has(data.state)}
            lifetime={lifetime}
          />
          <div className={styles.text}>
            {/* Только состояние: сколько подписок, видно на кнопке
                «Мои подписки · N», а дату показывает листок. */}
            <div className={styles.title}>
              {isGuest() ? 'Попробуйте бесплатно' : TITLES[data.state]}
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          {!hasAny ? (
            <Button variant="onHero" size="s" onClick={onOpenPlans}>
              Выбрать тариф
              <Icon name="chevron" size={16} />
            </Button>
          ) : !data.has_access || urgent ? (
            <>
              <Button variant="onHero" size="s" onClick={onOpenPlans}>
                {data.has_access ? 'Продлить' : 'Возобновить'}
                <Icon name="chevron" size={16} />
              </Button>
              <Button variant="quiet" size="s" onClick={onOpenSubscriptions}>
                Подписки
              </Button>
            </>
          ) : (
            <Button variant="onHero" size="s" onClick={onOpenSubscriptions}>
              {subsLabel}
              <Icon name="chevron" size={16} />
            </Button>
          )}
        </div>

        <Servers embedded />
      </section>

      {data.has_access ? (
        <StatGrid>
          <Stat icon="device" label="Устройства" value={devices} />
          <Stat
            icon="chart"
            label="Трафик"
            value={formatTraffic(data.traffic_used_bytes, data.traffic_limit_bytes)}
          />
        </StatGrid>
      ) : null}

      <ActionGrid>
        <ActionTile icon="users" title="Пригласить друга" hint="10% с покупок друзей" onClick={onOpenReferral} />
        <ActionTile
          icon="shield"
          title="Поддержка"
          hint="Ответим в чате"
          onClick={() => openExternal(config.supportUrl)}
        />
      </ActionGrid>

      <LegalLinks />
    </>
  );
}
